import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { TenantContextService } from 'app/@core/services/utility/tenant-context.service';
import { firstValueFrom } from 'rxjs';

/**
 * Ensures only users with the `system_admin` or `support` role can access a route.
 *
 * This guard runs AFTER AuthGuard and TenantGuard (which verify Firebase
 * authentication and tenant assignment). It gates the tenant-internal admin
 * module (user management, request templates, settings). support is included
 * because it has full system_admin parity except it can never create or act
 * on requests (see app-roles.interface.ts) — that boundary is enforced by
 * SupportCannotCreateRequests and the request-lifecycle CF checks, not by
 * this guard. Kept as `SystemAdminGuard` (not renamed) to avoid an unrelated
 * churn — the name predates support gaining this access.
 *
 * On failure: redirects to `/example` (the app's default authenticated landing).
 * Deliberately does not redirect to sign-in because the user IS authenticated —
 * they just lack the required role.
 *
 * Route wiring:
 *   {
 *     path: 'admin',
 *     canActivate: [AuthGuard, TenantGuard, SystemAdminGuard],
 *     loadChildren: () => import('app/modules/admin/admin.routes'),
 *   }
 */
export const SystemAdminGuard: CanActivateFn | CanActivateChildFn = async () => {
    const tenantCtx = inject(TenantContextService);
    const router    = inject(Router);

    const claims = await firstValueFrom(tenantCtx.claims$);

    if (claims?.role !== 'system_admin' && claims?.role !== 'support') {
        return router.parseUrl('example');
    }

    return true;
};
